import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import { countReleaseNoteItems, VERSION_STATES, type ReleaseNoteEditor, type ReleaseNoteEditorIndex, type ReleaseNoteItemType, type VersionState } from '@product-portal/shared';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/portal/pill';
import { Field, inputClass, NativeSelect } from '@/features/requests/request-form';
import { useAssociableRequests, useAssociateRequest, useCreateVersion, useDeleteVersion, usePublishReleaseNote, useReleaseNoteEditor, useReleaseNoteIndex, useSaveReleaseNote, useUpdateVersion } from '@/hooks/use-product';
import { ApiValidationError } from '@/lib/api';
import { formatDayMonth, formatRelative } from '@/lib/format';
import { countsLabel, RELEASE_NOTE_ITEM_LABELS, STATUS_LABELS, VERSION_STATE_LABELS } from '@/lib/labels';
import { cn } from '@/lib/utils';

interface ItemDraft {
  key: string;
  type: ReleaseNoteItemType;
  title: string;
  body: string;
  requestId: string;
}

interface Draft {
  title: string;
  summary: string;
  goodToKnow: string;
  items: ItemDraft[];
  closedRequestIds: string[];
}

let sequence = 0;
const newKey = () => `item-${(sequence += 1)}`;

function draftOf(editor: ReleaseNoteEditor | undefined): Draft {
  const note = editor?.note;
  return {
    title: note?.title ?? '',
    summary: note?.summary ?? '',
    goodToKnow: note?.goodToKnow ?? '',
    items: (note?.items ?? []).map((i) => ({ key: i.id, type: i.type, title: i.title, body: i.body, requestId: i.requestId ?? '' })),
    // A new note proposes every delivered request of the version, as the mock-up does.
    closedRequestIds: note ? note.closedRequestIds : (editor?.candidates ?? []).filter((c) => c.status === 'delivered').map((c) => c.id),
  };
}

function ItemsEditor({
  type,
  draft,
  setDraft,
  candidates,
  disabled,
}: {
  type: ReleaseNoteItemType;
  draft: Draft;
  setDraft: (update: (d: Draft) => Draft) => void;
  candidates: ReleaseNoteEditor['candidates'];
  disabled: boolean;
}) {
  const items = draft.items.filter((i) => i.type === type);
  const update = (key: string, patch: Partial<ItemDraft>) => setDraft((d) => ({ ...d, items: d.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) }));
  const moveItem = (key: string, delta: -1 | 1) =>
    setDraft((d) => {
      const same = d.items.filter((i) => i.type === type);
      const index = same.findIndex((i) => i.key === key);
      const target = same[index + delta];
      if (!target) return d;
      const all = [...d.items];
      const a = all.findIndex((i) => i.key === key);
      const b = all.findIndex((i) => i.key === target.key);
      [all[a], all[b]] = [all[b]!, all[a]!];
      return { ...d, items: all };
    });
  const label = type === 'feature' ? 'Nouveautés' : 'Corrections';
  return (
    <fieldset className="m-0 flex flex-col gap-2.5 border-0 p-0" disabled={disabled}>
      <legend className="mb-2 flex items-center gap-2 p-0 text-sm font-bold">
        {label}
        <span className="font-normal text-muted-foreground">
          {countsLabel(countReleaseNoteItems(items)) || 'aucune'}
        </span>
      </legend>
      {items.map((item, index) => (
        <div key={item.key} className="flex flex-col gap-2 rounded-control border border-border bg-surface-subtle p-3">
          <div className="flex items-center gap-2">
            <input
              aria-label={`Titre de la ${RELEASE_NOTE_ITEM_LABELS[type].toLowerCase()} ${index + 1}`}
              value={item.title}
              onChange={(e) => update(item.key, { title: e.target.value })}
              placeholder="Titre, affiché en gras"
              className={cn(inputClass, 'min-h-10 flex-1 font-bold')}
            />
            <button type="button" aria-label="Monter" disabled={index === 0} onClick={() => moveItem(item.key, -1)} className="cursor-pointer border-0 bg-transparent p-1 text-muted-foreground disabled:opacity-30">
              <ArrowUp className="size-4" />
            </button>
            <button type="button" aria-label="Descendre" disabled={index === items.length - 1} onClick={() => moveItem(item.key, 1)} className="cursor-pointer border-0 bg-transparent p-1 text-muted-foreground disabled:opacity-30">
              <ArrowDown className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Retirer"
              onClick={() => setDraft((d) => ({ ...d, items: d.items.filter((i) => i.key !== item.key) }))}
              className="cursor-pointer border-0 bg-transparent p-1 text-muted-foreground"
            >
              <X className="size-4" />
            </button>
          </div>
          <textarea
            aria-label="Texte"
            rows={2}
            value={item.body}
            onChange={(e) => update(item.key, { body: e.target.value })}
            placeholder="Texte (facultatif)"
            className={cn(inputClass, 'resize-y py-2.5')}
          />
          <NativeSelect id={`demande-${item.key}`} aria-label="Demande liée" value={item.requestId} onChange={(e) => update(item.key, { requestId: e.target.value })} className="min-h-10 text-[13px]">
            <option value="">Aucune demande liée</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                Répond à {c.reference} · {c.title}
              </option>
            ))}
          </NativeSelect>
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="self-start"
        onClick={() => setDraft((d) => ({ ...d, items: [...d.items, { key: newKey(), type, title: '', body: '', requestId: '' }] }))}
      >
        <Plus aria-hidden="true" className="size-4" /> Ajouter {type === 'feature' ? 'une nouveauté' : 'une correction'}
      </Button>
    </fieldset>
  );
}

/**
 * « Demandes de la version » (2026-10-24): the requests whose target version is
 * this one, any status; « Associer une demande » makes the version their target
 * (as screen 08). A ticked request is closed by the note: delivered at publication
 * if it is not yet, and its requester and subscribers are told.
 */
function VersionRequests({
  versionId,
  label,
  candidates,
  closedIds,
  published,
  setClosed,
}: {
  versionId: string;
  label: string;
  candidates: ReleaseNoteEditor['candidates'];
  closedIds: string[];
  published: boolean;
  setClosed: (id: string, closed: boolean) => void;
}) {
  const [q, setQ] = useState('');
  const found = useAssociableRequests(versionId, q);
  const associate = useAssociateRequest(versionId);
  return (
    <section aria-labelledby="version-requests-title" className="flex flex-col gap-3 rounded-card border border-border bg-card px-5 py-4">
      <h2 id="version-requests-title" className="m-0 text-[15px] font-bold">
        Demandes de la version
      </h2>
      <p className="m-0 text-[13px] text-foreground-secondary">
        Demandes corrigées ou traitées par {label}. Les cochées sont clôturées par la note : livrées à la publication et leurs demandeurs prévenus.
      </p>
      {candidates.length === 0 && <p className="m-0 text-[13px] text-muted-foreground">Aucune demande associée à cette version.</p>}
      {candidates.map((c) => (
        <div key={c.id} className="flex items-start gap-2.5 text-[13px]">
          <input
            type="checkbox"
            id={`clos-${c.id}`}
            aria-label={`Clôturer ${c.reference} par la note`}
            disabled={published}
            checked={closedIds.includes(c.id)}
            onChange={(e) => setClosed(c.id, e.target.checked)}
            className="mt-0.5"
          />
          <label htmlFor={`clos-${c.id}`} className="flex min-w-0 flex-1 flex-col">
            <strong>{c.title}</strong>
            <span className="text-xs text-muted-foreground">
              {c.reference} · {c.teamName} · {STATUS_LABELS[c.status]}
            </span>
          </label>
          {!published && (
            <button
              type="button"
              aria-label={`Retirer ${c.reference} de la version`}
              title="Retirer de la version"
              disabled={associate.isPending}
              onClick={() => {
                setClosed(c.id, false);
                associate.mutate({ requestId: c.id, associated: false });
              }}
              className="cursor-pointer border-0 bg-transparent p-0.5 text-muted-foreground hover:text-destructive"
            >
              <X aria-hidden="true" className="size-4" />
            </button>
          )}
        </div>
      ))}
      {!published && (
        <div className="flex flex-col gap-1.5 border-t border-border pt-3">
          <label htmlFor="associer" className="text-[13px] font-bold">
            Associer une demande
          </label>
          <input id="associer" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Titre ou référence (PP-…)" className={cn(inputClass, 'min-h-9 text-[13px]')} />
          {q.trim().length >= 2 && (found.data ?? []).length === 0 && !found.isFetching && <span className="text-xs text-muted-foreground">Aucune demande de ce produit ne correspond.</span>}
          {(found.data ?? []).map((r) => (
            <button
              key={r.id}
              type="button"
              disabled={associate.isPending}
              onClick={() => associate.mutate({ requestId: r.id, associated: true }, { onSuccess: () => setClosed(r.id, true) })}
              className="flex cursor-pointer items-center justify-between gap-2 rounded-control border border-border bg-card px-3 py-1.5 text-left font-[inherit] text-[13px] hover:bg-accent"
            >
              <span className="min-w-0">
                <strong>{r.reference}</strong> · {r.title}
                <span className="block text-xs text-muted-foreground">
                  {r.teamName} · {STATUS_LABELS[r.status]}
                </span>
              </span>
              <span className="shrink-0 text-xs font-bold text-primary">Associer</span>
            </button>
          ))}
          {associate.error && <span className="text-xs font-bold text-destructive">{associate.error.message}</span>}
        </div>
      )}
    </section>
  );
}

/** Why a version cannot be deleted (ADR-012), or null when it can. */
function deletionBlocker(v: ReleaseNoteEditorIndex['products'][number]['versions'][number]): string | null {
  const reasons = [
    v.requestCount > 0 && `${v.requestCount} demande${v.requestCount > 1 ? 's la ciblent' : ' la cible'}`,
    v.noteState !== null && 'elle a une release note',
  ].filter(Boolean);
  return reasons.length ? `Suppression impossible : ${reasons.join(' et ')}.` : null;
}

/**
 * Versions of the product (ADR-012, formerly on screen 12): number, planned
 * date, state changed by hand (ADR-005 §2). Deletion only while no request
 * targets the version and it has no release note; the API checks it again.
 */
function VersionsSection({ product, onDeleted }: { product: ReleaseNoteEditorIndex['products'][number]; onDeleted: (id: string) => void }) {
  const create = useCreateVersion();
  const update = useUpdateVersion();
  const remove = useDeleteVersion();
  const [number, setNumber] = useState('');
  const [plannedDate, setPlannedDate] = useState('');
  const error = create.error ?? update.error ?? remove.error;
  const errorText = error instanceof ApiValidationError ? error.issues.map((i) => i.message).join(' ') : error?.message;
  return (
    <section aria-labelledby="versions-title" className="flex flex-col gap-3 rounded-card border border-border bg-card px-5 py-4">
      <h2 id="versions-title" className="m-0 text-[15px] font-bold">
        Versions · {product.name}
      </h2>
      {product.versions.length === 0 && <p className="m-0 text-[13px] text-muted-foreground">Aucune version : ajoutez la première ci-dessous.</p>}
      {product.versions.map((v) => {
        const blocker = deletionBlocker(v);
        return (
          <div key={v.id} className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.3fr)_minmax(0,1.5fr)_auto] items-center gap-2 text-[13px]">
            <strong className="truncate">{v.number}</strong>
            <input
              type="date"
              aria-label={`Date prévue de ${v.number}`}
              value={v.plannedDate ?? ''}
              onChange={(e) => update.mutate({ id: v.id, plannedDate: e.target.value || null })}
              className={cn(inputClass, 'min-h-9 px-2 text-[13px]')}
            />
            <NativeSelect
              id={`etat-${v.id}`}
              aria-label={`État de ${v.number}`}
              value={v.state}
              onChange={(e) => update.mutate({ id: v.id, state: e.target.value as VersionState })}
              className="min-h-9 text-[13px]"
            >
              {VERSION_STATES.map((state) => (
                <option key={state} value={state}>
                  {VERSION_STATE_LABELS[state]}
                </option>
              ))}
            </NativeSelect>
            <button
              type="button"
              aria-label={`Supprimer la version ${v.number}`}
              title={blocker ?? `Supprimer la version ${v.number}`}
              disabled={blocker !== null || remove.isPending}
              onClick={() => {
                if (window.confirm(`Supprimer la version ${product.name} ${v.number} ?`)) remove.mutate(v.id, { onSuccess: () => onDeleted(v.id) });
              }}
              className="cursor-pointer border-0 bg-transparent p-1 text-muted-foreground hover:text-destructive disabled:cursor-not-allowed disabled:opacity-30"
            >
              <Trash2 aria-hidden="true" className="size-4" />
            </button>
          </div>
        );
      })}
      <form
        className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.3fr)_auto] items-center gap-2 border-t border-border pt-3"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate(
            { productId: product.id, number, plannedDate: plannedDate || null },
            {
              onSuccess: () => {
                setNumber('');
                setPlannedDate('');
              },
            },
          );
        }}
      >
        <input aria-label="Numéro de la nouvelle version" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="ex. 3.6" className={cn(inputClass, 'min-h-9 text-[13px]')} />
        <input type="date" aria-label="Date prévue de la nouvelle version" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} className={cn(inputClass, 'min-h-9 px-2 text-[13px]')} />
        <Button type="submit" variant="secondary" size="sm" disabled={!number.trim() || create.isPending}>
          <Plus aria-hidden="true" className="size-4" /> Ajouter
        </Button>
      </form>
      <p className="m-0 text-xs text-muted-foreground">Une version ciblée par une demande ou dotée d'une release note ne peut pas être supprimée.</p>
      {errorText && (
        <p role="alert" className="m-0 text-xs font-bold text-destructive">
          {errorText}
        </p>
      )}
    </section>
  );
}

/**
 * 13 · Rédaction d'une release note, by version. New features and fixes are
 * ITEMS (ADR-005 §5, where mock-up 13 shows two text areas): the counters of
 * screen 05 are computed from them. Draft, then publication, which notifies.
 */
const NEW_VERSION = '__new__';

export function ReleaseNoteEditorPage() {
  const index = useReleaseNoteIndex();
  const products = index.data?.products ?? [];
  const [productId, setProductId] = useState('');
  const [versionId, setVersionId] = useState('');
  const product = products.find((p) => p.id === productId) ?? products[0];
  // Default: the first version without a published note, else the last one.
  const version = product?.versions.find((v) => v.id === versionId) ?? product?.versions.find((v) => v.noteState !== 'published') ?? product?.versions.at(-1);
  const editor = useReleaseNoteEditor(version?.id ?? null);
  const createVersion = useCreateVersion();
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const [newVersionDate, setNewVersionDate] = useState('');
  const addVersion = () => {
    if (!product || !newVersion?.trim()) return;
    createVersion.mutate(
      { productId: product.id, number: newVersion.trim(), plannedDate: newVersionDate || null },
      {
        onSuccess: ({ id }) => {
          setVersionId(id);
          setNewVersion(null);
          setNewVersionDate('');
        },
      },
    );
  };
  const save = useSaveReleaseNote(version?.id ?? '');
  const publish = usePublishReleaseNote(version?.id ?? '');
  const [draft, setDraftState] = useState<Draft>(draftOf(undefined));
  const [preview, setPreview] = useState(false);
  // Restart from the server on another version or a saved note only: associating a request
  // (which changes the candidates) must not wipe the draft being written.
  const serverKey = JSON.stringify(editor.data ? { version: editor.data.version.id, note: editor.data.note } : null);
  useEffect(() => setDraftState(draftOf(editor.data)), [serverKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const setDraft = (update: (d: Draft) => Draft) => setDraftState(update);
  const published = editor.data?.note?.state === 'published';
  const payload = useMemo(
    () => ({
      title: draft.title,
      summary: draft.summary,
      goodToKnow: draft.goodToKnow,
      items: draft.items.map((i) => ({ type: i.type, title: i.title, body: i.body, requestId: i.requestId || null })),
      closedRequestIds: draft.closedRequestIds,
    }),
    [draft],
  );
  const error = save.error ?? publish.error;
  const errorText = error instanceof ApiValidationError ? error.issues.map((i) => i.message).join(' ') : error?.message;
  const counts = countReleaseNoteItems(draft.items);

  return (
    <>
      <div className="flex flex-col gap-2.5">
        <Link to="/release-notes" className="text-[13px] no-underline">
          ← Release notes publiées
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="m-0 text-[26px] font-black tracking-[-0.01em]">
              Release note · {product?.name ?? ''} {version?.number ?? ''}
            </h1>
            <p className="m-0 text-sm text-muted-foreground">
              {editor.data?.note
                ? published
                  ? `Publiée ${formatRelative(editor.data.note.publishedAt!).toLowerCase()}`
                  : `Brouillon enregistré ${formatRelative(editor.data.note.updatedAt).toLowerCase()}`
                : 'Pas encore de brouillon'}
              {version?.plannedDate && ` · livraison prévue le ${formatDayMonth(version.plannedDate)}`}
              {version && ` · ${VERSION_STATE_LABELS[version.state].toLowerCase()}`}
            </p>
          </div>
          <Pill className={published ? 'bg-status-delivered text-status-delivered-foreground' : 'bg-warning text-warning-foreground'}>{published ? 'Publiée' : 'Brouillon'}</Pill>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-6">
        <form
          className="flex min-w-0 flex-[999_1_560px] flex-col gap-5 rounded-card border border-border bg-card p-6"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate(payload);
          }}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="rn-produit" label="Produit">
              <NativeSelect
                id="rn-produit"
                value={product?.id ?? ''}
                onChange={(e) => {
                  setProductId(e.target.value);
                  setVersionId('');
                }}
              >
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field id="rn-version" label="Version">
              <NativeSelect
                id="rn-version"
                value={newVersion !== null ? NEW_VERSION : (version?.id ?? '')}
                onChange={(e) => (e.target.value === NEW_VERSION ? setNewVersion('') : (setNewVersion(null), setVersionId(e.target.value)))}
              >
                {!version && <option value="">Aucune version</option>}
                {(product?.versions ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.number} · {v.plannedDate ? formatDayMonth(v.plannedDate) : VERSION_STATE_LABELS[v.state].toLowerCase()}
                    {v.noteState === 'published' ? ' · publiée' : v.noteState === 'draft' ? ' · brouillon' : ''}
                  </option>
                ))}
                <option value={NEW_VERSION}>+ Nouvelle version…</option>
              </NativeSelect>
            </Field>
          </div>
          {/* Created from the list itself (2026-10-24), as in « Versions » on the right. */}
          {newVersion !== null && product && (
            <div className="-mt-2 flex flex-col gap-1.5">
              <div className="flex flex-wrap gap-2">
                <input
                  aria-label={`Numéro de la nouvelle version de ${product.name}`}
                  autoFocus
                  value={newVersion}
                  onChange={(e) => setNewVersion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addVersion();
                    }
                  }}
                  placeholder="ex. 3.6"
                  className={cn(inputClass, 'min-h-10 min-w-0 flex-1')}
                />
                <input type="date" aria-label="Date prévue de la nouvelle version" value={newVersionDate} onChange={(e) => setNewVersionDate(e.target.value)} className={cn(inputClass, 'min-h-10 w-44')} />
                <Button type="button" variant="secondary" disabled={!newVersion.trim() || createVersion.isPending} onClick={addVersion}>
                  Créer la version
                </Button>
                <Button type="button" variant="ghost" onClick={() => setNewVersion(null)}>
                  Annuler
                </Button>
              </div>
              {createVersion.error && <span className="text-xs font-bold text-destructive">{createVersion.error.message}</span>}
            </div>
          )}
          {product && product.versions.length === 0 && newVersion === null && (
            <p className="m-0 text-[13px] text-muted-foreground">Aucune version pour ce produit : choisissez « + Nouvelle version… » dans la liste.</p>
          )}

          <fieldset disabled={published || !version} className="m-0 flex flex-col gap-5 border-0 p-0">
            <Field id="rn-titre" label="Titre">
              <input id="rn-titre" type="text" value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} className={inputClass} />
            </Field>
            <Field id="rn-resume" label="Résumé" hint="Affiché dans la liste des release notes et dans l'email aux abonnés.">
              <textarea id="rn-resume" rows={3} value={draft.summary} onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))} className={cn(inputClass, 'resize-y py-3')} />
            </Field>
            <ItemsEditor type="feature" draft={draft} setDraft={setDraft} candidates={editor.data?.candidates ?? []} disabled={published} />
            <ItemsEditor type="fix" draft={draft} setDraft={setDraft} candidates={editor.data?.candidates ?? []} disabled={published} />
            <Field id="rn-info" label="À savoir">
              <textarea id="rn-info" rows={2} value={draft.goodToKnow} onChange={(e) => setDraft((d) => ({ ...d, goodToKnow: e.target.value }))} className={cn(inputClass, 'resize-y py-3')} />
            </Field>
          </fieldset>

          {errorText && (
            <p role="alert" className="m-0 text-sm font-bold text-destructive">
              {errorText}
            </p>
          )}
          {preview && (
            <div className="flex flex-col gap-1 rounded-control border border-border bg-surface-subtle px-4 py-3 text-[13px]">
              <strong>Aperçu de la liste (écran 05)</strong>
              <span>
                {product?.name} {version?.number} · {countsLabel(counts) || 'aucun item'}
              </span>
              <span className="text-muted-foreground">{draft.summary || 'Sans résumé'}</span>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
            <div className="flex gap-2.5">
              <Button type="submit" variant="secondary" disabled={published || !version || save.isPending}>
                Enregistrer le brouillon
              </Button>
              <Button type="button" variant="ghost" onClick={() => setPreview((p) => !p)}>
                Aperçu
              </Button>
            </div>
            <Button
              type="button"
              disabled={published || !version || save.isPending || publish.isPending || !draft.title.trim()}
              onClick={() => save.mutate(payload, { onSuccess: () => publish.mutate() })}
            >
              Publier et notifier les abonnés
            </Button>
          </div>
        </form>

        <aside className="flex min-w-0 flex-[1_1_320px] flex-col gap-6">
          {product && <VersionsSection product={product} onDeleted={(id) => id === versionId && setVersionId('')} />}
          {version && (
            <VersionRequests
              versionId={version.id}
              label={`${product?.name ?? ''} ${version.number}`}
              candidates={editor.data?.candidates ?? []}
              closedIds={draft.closedRequestIds}
              published={published}
              setClosed={(id, closed) =>
                setDraft((d) => ({ ...d, closedRequestIds: closed ? [...new Set([...d.closedRequestIds, id])] : d.closedRequestIds.filter((x) => x !== id) }))
              }
            />
          )}
          <section className="flex flex-col gap-2.5 rounded-card border border-border bg-card px-5 py-4">
            <h2 className="m-0 text-[15px] font-bold">À la publication</h2>
            <ul className="m-0 flex flex-col gap-1.5 pl-5 text-[13px] text-foreground-secondary">
              <li>La note apparaît dans Release notes.</li>
              <li>Les demandes cochées qui ne sont pas encore livrées passent en Livrée.</li>
              <li>
                Les demandeurs et abonnés des {draft.closedRequestIds.length} demande{draft.closedRequestIds.length > 1 ? 's' : ''} cochée
                {draft.closedRequestIds.length > 1 ? 's' : ''} sont notifiés.
              </li>
              {/* OPEN-QUESTION delivery-subscription: no subscription to a product exists (« les 38 abonnés au produit » of the mock-up). */}
            </ul>
          </section>
        </aside>
      </div>
    </>
  );
}
